import log from "loglevel"
import { downloadFile, prettyJson } from "../tools"
import { useAppErrorStore } from "../zstore/AppErrorStore"
import { useAuthStore } from "../zstore/AuthStore"

const localServerHost: string | undefined = import.meta.env.VITE_LOCAL_SERVER_HOST

const origin = window.location.origin
let apiOrigin: string

if (origin.includes("localhost") || origin.includes("127.0.0.1")) {
    // Local dev backend
    apiOrigin = localServerHost ?? "http://127.0.0.1:8010"
} else {
    // URL will include "nexus.nv5geospatial.com" by convention
    const addApiIndex = origin.indexOf("nexus.nv5geospatial.com")
    if (addApiIndex === -1) {
        throw new Error(`Couldn't find index for nexus.nv5geospatial.com in the current origin: ${origin}`)
    }
    // Insert the "api" subdomain at the correct location in other environments
    apiOrigin = `${origin.slice(0, addApiIndex)}api.${origin.slice(addApiIndex)}`
}

export type NexusResponse<T> = NexusSuccessResponse<T> | NexusErrorResponse

export interface NexusFileSuccessResponse {
    success: true
}

export interface NexusSuccessResponse<T> {
    data: T
    success: true
    message?: string
}

export interface NexusErrorResponse {
    success: false
    /**
     * Http Status Code
     */
    error: number
    message?: string
}

const nexusBackendUnreachableError = "Couldn't connect to Nexus."

export class API {
    static get = async <T>(path: string, defaultMessage: string): Promise<NexusSuccessResponse<T>> => {
        return nexusFetch({
            path,
            defaultMessage,
            method: "GET",
            includeJson: true,
            includeClient: true,
        })
    }

    static post = async <T>(
        path: string,
        requestObject: object,
        defaultMessage: string,
    ): Promise<NexusSuccessResponse<T>> => {
        return nexusFetch({
            path,
            defaultMessage,
            method: "POST",
            requestObject,
            includeJson: true,
            includeClient: true,
        })
    }

    static put = async <T, O extends object>(
        path: string,
        requestObject: O,
        defaultMessage: string,
    ): Promise<NexusSuccessResponse<T>> => {
        return nexusFetch({
            path,
            defaultMessage,
            method: "PUT",
            requestObject,
            includeJson: true,
            includeClient: true,
        })
    }

    static delete = async <T>(path: string, defaultMessage: string): Promise<NexusSuccessResponse<T>> => {
        return nexusFetch({
            path,
            defaultMessage,
            method: "DELETE",
            includeJson: true,
            includeClient: true,
        })
    }

    static downloadFile = async (path: string): Promise<NexusFileSuccessResponse> => {
        const headers = await getHeaders({ includeClient: true, includeFileDownload: true })

        let response
        try {
            response = await fetch(`${apiOrigin}${path}`, { method: "GET", headers })
        } catch (error) {
            log.error(error)
            throw new Error(nexusBackendUnreachableError)
        }

        return handleFileResponse(response)
    }

    static uploadFile = async <T>(
        path: string,
        formData: FormData,
        args?: {
            repair_polygons?: boolean
        },
        defaultMessage?: string,
    ): Promise<NexusSuccessResponse<T>> => {
        const queryParams = args ? `?${Object.entries(args).map(([key, val]) => `${key}=${val}`)}` : ""

        return nexusFetch({
            path: `${path}${queryParams}`,
            defaultMessage: defaultMessage ?? "Couldn't upload file.",
            method: "POST",
            includeClient: true,
            formData,
        })
    }

    // TODO We have one request that doesn't return the standard Nexus Response. Make that request consistent with
    // TODO other nexus requests.
    static getRawResponse = async <T>(path: string, defaultMessage?: string): Promise<T> => {
        const headers = await getHeaders({ includeJson: true, includeClient: true })

        let response
        try {
            response = await fetch(`${apiOrigin}${path}`, { method: "GET", headers })
        } catch (error) {
            log.error(error)
            throw new Error(nexusBackendUnreachableError)
        }

        if (response.status >= 400) {
            if (response.status >= 500) {
                // Unexpected Server error. Let's log, display the app's global error message and throw.
                // 4XX errors should be handled in individual requests, as they report user facing errors.
                useAppErrorStore.getState().setAppError(defaultMessage)
                throw new Error(defaultMessage)
            }
            // 4XX errors should be handled in individual requests, as they report user facing errors.
            throw new Error(response.statusText ?? defaultMessage)
        }
        return await response.json()
    }
}

type HeadersType = { [key: string]: string }

const getHeaders = async (config: {
    includeJson?: boolean
    includeClient?: boolean
    includeFileDownload?: boolean
}): Promise<Headers> => {
    const { includeJson, includeClient, includeFileDownload } = config

    const headers: HeadersType = {}

    const authState = useAuthStore.getState()
    const fresh = await authState.checkTokenExpiration()
    if (fresh) {
        headers.Authorization = `Bearer ${authState.insiteToken}`
    } else {
        log.debug("No fresh auth headers available.")
    }

    if (includeClient) {
        headers.Client = authState.nexusClient ?? ""
    }
    if (includeJson) {
        headers["Content-Type"] = "application/json"
        headers.Accept = "application/json"
    }
    if (includeFileDownload) {
        headers.Accept = "Application/octet-stream"
    }
    return new Headers(headers)
}

interface NexusFetchConfig {
    path: string
    method: "GET" | "POST" | "PUT" | "DELETE"
    defaultMessage: string
    requestObject?: object
    formData?: FormData
    includeJson?: boolean
    includeClient?: boolean
    includeFileDownload?: boolean
}

const nexusFetch = async <T>({
    path,
    method,
    defaultMessage,
    requestObject,
    formData,
    includeJson,
    includeClient,
    includeFileDownload,
}: NexusFetchConfig): Promise<NexusSuccessResponse<T>> => {
    const headers = await getHeaders({
        includeJson,
        includeClient,
        includeFileDownload,
    })

    let body

    if (requestObject != null) {
        body = JSON.stringify(requestObject)
    } else if (formData != null) {
        body = formData
    }

    let response
    try {
        response = await fetch(`${apiOrigin}${path}`, {
            method,
            headers,
            body,
        })
    } catch (error) {
        log.error(error)
        throw new Error(nexusBackendUnreachableError)
    }

    // Response type 204 has no content by default, so running response.json() throws an error
    const result: NexusResponse<T> =
        response.status === 204 ? { success: true, data: undefined } : await response.json()

    // TODO Remove result.success and result.error. Use HTTP status codes only.
    if (response.status >= 400 || !result.success) {
        if (response.status >= 500 || (!result.success && result.error >= 500)) {
            // Unexpected Server error. Let's log, display the app's global error message and throw.
            // 4XX errors should be handled in individual requests, as they report user facing errors.
            log.error(`${defaultMessage} Response status: ${response.status} Response: ${prettyJson(result)}`)
            useAppErrorStore.getState().setAppError(defaultMessage)
            throw new Error(defaultMessage)
        }
        // 4XX errors should be handled in individual requests, as they report user facing errors.
        log.error(
            response.status >= 400
                ? `Received status code ${response.status}`
                : `Received response with success: ${result.success}`,
        )
        throw new Error(result.message ?? defaultMessage)
    }
    return result
}

const handleFileResponse = async (response: Response): Promise<NexusFileSuccessResponse> => {
    const result = await response.blob()
    const contentDisposition = response.headers.get("content-disposition")

    if (response.status >= 400) {
        throw new Error(`${response.statusText}`)
    }

    downloadFile(result, contentDisposition?.split("filename=")[1])
    return { success: true }
}
